import React, { useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SectionTitle from "../components/SectionTitle";
import Timeline from "../components/Timeline";
import CTA from "../components/CTA";
import {
  FloatingFurniture,
  Sawdust,
  WoodRings,
  DovetailDivider,
  PendulumLamp,
  SET_A,
  SET_B,
} from "../components/Decor";
import useSmoothScroll from "../hooks/useSmoothScroll";
import useScrollFx from "../hooks/useScrollFx";
import { ICONS } from "../data/content";
import "../css/About.css";
import "../css/AboutFx.css";
import "../css/HomeDecor.css"; // shared furniture decor styles (import last)

const JOURNEY_STEPS = [
  {
    icon: "1990",
    title: "The Workshop Begins",
    description:
      "Founded in Palakkad, building furniture for local homes with a small team of carpenters.",
  },
  {
    icon: "2000s",
    title: "Growing Reputation",
    description:
      "Word-of-mouth trust grew our client base across Palakkad and neighbouring districts.",
  },
  {
    icon: "2010s",
    title: "Interiors & Custom Work",
    description:
      "Expanded into wardrobes, kitchens, TV units, panel works and staircases, all fully customised.",
  },
  {
    icon: "Today",
    title: "Serving All India",
    description:
      "More than three decades on, we take orders from across the country and still build each piece with care.",
  },
];

const VALUES = [
  {
    icon: ICONS.wood,
    title: "Craftsmanship",
    description:
      "Every joint, cut, and finish is done by trained hands, not shortcuts.",
  },
  {
    icon: ICONS.experience,
    title: "Quality",
    description:
      "We use seasoned, solid timber built to withstand daily use for decades.",
  },
  {
    icon: ICONS.design,
    title: "Integrity",
    description:
      "Honest timelines, transparent pricing, and furniture that matches what was promised.",
  },
  {
    icon: ICONS.delivery,
    title: "Customer Satisfaction",
    description:
      "We measure success by how long our furniture lasts in your home.",
  },
];

const WORKSHOP_IMAGES = [
  "/assets/w1.png",
  "/assets/w2.png",
  "/assets/w3.png",
  "/assets/w4.png",
];

const GALLERY_SPEEDS = [0.08, -0.06, 0.12, -0.04];

function ValueCard({ icon, title, description }) {
  return (
    <div className="feature-card ab-value">
      <div className="feature-card__icon">{icon}</div>
      <h3 style={{color: "brown"}}>{title}</h3>
      <p>{description}</p>
    </div>
  );
}

export default function About() {
  const root = useRef(null);
  useSmoothScroll();

  useScrollFx(root, (gsap) => {
    gsap.registerPlugin(ScrollTrigger);

    const scope = root.current;
    if (!scope) return;

    // ---------- HERO INTRO ----------
    const tl = gsap.timeline({ defaults: { ease: "power4.out" } });
    tl.from(".about-hero__img", {
      scale: 1.35,
      duration: 2,
      ease: "power3.out",
    })
      .from(
        ".about-hero .hero__eyebrow",
        { y: 20, opacity: 0, duration: 0.8 },
        0.3,
      )
      .from(
        ".ab-line > span",
        { yPercent: 115, duration: 1.2, stagger: 0.14 },
        0.4,
      )
      .from(
        ".about-hero__content p",
        { y: 30, opacity: 0, duration: 0.9 },
        1.0,
      );

    // ---------- HERO PARALLAX ----------
    const heroEl = scope.querySelector(".about-hero");
    if (heroEl) {
      const st = {
        trigger: heroEl,
        start: "top top",
        end: "bottom top",
        scrub: true,
      };
      gsap.to(".about-hero__img", {
        yPercent: 22,
        scale: 1.18,
        ease: "none",
        scrollTrigger: st,
      });
      gsap.to(".about-hero__content", {
        yPercent: -20,
        opacity: 0,
        ease: "none",
        scrollTrigger: st,
      });
    }

    // ---------- TIMELINE ----------
    scope.querySelectorAll(".timeline-step").forEach((step) => {
      const year = step.querySelector(".timeline-year");
      const tlStep = gsap.timeline({
        scrollTrigger: { trigger: step, start: "top 88%", once: true },
      });
      tlStep.from(step, {
        x: -70,
        opacity: 0,
        duration: 0.9,
        ease: "power3.out",
        clearProps: "transform,opacity",
      });
      if (year) {
        tlStep.from(
          year,
          {
            scale: 0.4,
            opacity: 0,
            duration: 0.6,
            ease: "back.out(2)",
            clearProps: "transform,opacity",
          },
          0.15,
        );
      }
    });

    // ---------- VALUES ----------
    const valuesSection = scope.querySelector(".ab-values");
    const valueCards = scope.querySelectorAll(".ab-value");
    if (valuesSection && valueCards.length) {
      gsap.from(valueCards, {
        y: 80,
        opacity: 0,
        scale: 0.94,
        duration: 0.9,
        stagger: 0.12,
        ease: "power3.out",
        clearProps: "transform,opacity",
        scrollTrigger: { trigger: valuesSection, start: "top 85%", once: true },
      });
    }

    // ---------- FURNITURE-THEMED EFFECTS ----------
    // Tape-measure progress bar
    gsap.to(".scroll-progress", {
      scaleX: 1,
      ease: "none",
      scrollTrigger: { start: 0, end: "max", scrub: 0.3 },
    });

    // Dovetail joints draw themselves
    scope.querySelectorAll(".dovetail path").forEach((p) => {
      const len = p.getTotalLength();
      gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
      gsap.to(p, {
        strokeDashoffset: 0,
        duration: 2.2,
        ease: "power2.inOut",
        scrollTrigger: { trigger: p, start: "top 95%", once: true },
      });
    });

    // Tree rings rotate with scroll
    scope.querySelectorAll("[data-spin]").forEach((el) => {
      gsap.to(el, {
        rotation: parseFloat(el.dataset.spin),
        ease: "none",
        scrollTrigger: {
          trigger: el.parentElement,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    });

    // Furniture line-art drifts for depth
    scope.querySelectorAll(".decor-float").forEach((el, i) => {
      const dir = i % 2 ? 1 : -1;
      gsap.fromTo(
        el,
        { y: 70 * dir },
        {
          y: -70 * dir,
          ease: "none",
          scrollTrigger: {
            trigger: el.closest("section") || el.parentElement,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        },
      );
    });
  });

  return (
    <div ref={root}>
      <div className="scroll-progress" aria-hidden="true" />

      <section className="about-hero">
        <img
          className="about-hero__img"
          src="https://images.unsplash.com/photo-1449247709967-d4461a6a6103?auto=format&fit=crop&w=1920&q=80"
          alt="Wooden furniture workshop interior with timber stacks"
          loading="eager"
        />
        <div className="about-hero__overlay" />
        <Sawdust count={22} />
        <PendulumLamp />
        <div className="container about-hero__content">
          <span className="hero__eyebrow">About Anappuram</span>
          <h1>
            <span className="ab-line">
              <span>Our Journey in Wood,</span>
            </span>
            <span className="ab-line">
              <span className="text-accent">Since 1990</span>
            </span>
          </h1>
          <p>
            Over three decades of handcrafted furniture and interiors, built
            with patience and precision in Palakkad, and delivered
            across India.
          </p>
        </div>
      </section>

      <section className="section ab-section decor-host">
        <div className="decor-grain" aria-hidden="true" />
        <WoodRings />
        <span className="ab-bigword" data-drift="-16" aria-hidden="true">
          Since 1990 · Since 1990
        </span>
        <div className="container about-story__grid">
          <div className="about-story__media fx-clip">
            <img
              data-parallax="0.12"
              src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=900&q=80"
              alt="Founder-era carpentry workshop"
              loading="lazy"
            />
          </div>
          <div className="about-story__content" data-reveal="right">
            <SectionTitle
              eyebrow="Our Story"
              title="Built on Craft, Grown on Trust"
            />
            <p>
              Anappuram Wooden Furniture Work started in 1990 as a small
              carpentry workshop in Palakkad, on a simple belief:
              furniture should be built to last generations, not seasons. Today
              we make wardrobes, kitchen units, TV units, interior panel works,
              staircases and every kind of custom furniture. Our orders now come
              from all over the country.
            </p>
            <div className="about-story__mv" data-stagger>
              <div className="ab-card">
                <h4>Our Mission</h4>
                <p>
                  To craft durable, beautifully finished wooden furniture that
                  fits every home and budget, without compromising on quality.
                </p>
              </div>
              <div className="ab-card">
                <h4>Our Vision</h4>
                <p>
                  To remain Palakkad's most trusted name in custom wooden
                  furniture, known for craftsmanship passed down through
                  generations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DovetailDivider />

      <section
        className="section decor-host"
        style={{ backgroundColor: "#f2ece1" }}
      >
        <div className="decor-grain" aria-hidden="true" />
        <FloatingFurniture items={SET_A} />
        <div className="container">
          <SectionTitle eyebrow="Since 1990" title="Our Journey" center />
          <Timeline
            steps={JOURNEY_STEPS.map((s) => ({
              ...s,
              icon: <span className="timeline-year">{s.icon}</span>,
            }))}
            vertical
          />
        </div>
      </section>

      <DovetailDivider flip />

      <section className="section decor-host">
        <div className="decor-grain" aria-hidden="true" />
        <FloatingFurniture items={SET_B} />
        <div className="container">
          <SectionTitle
            eyebrow="Inside The Workshop"
            title="Workshop Gallery"
            center
          />
          <div className="workshop-gallery ab-gallery">
            {WORKSHOP_IMAGES.map((src, i) => (
              <div
                className="ab-gallery__col"
                key={src}
                data-parallax={GALLERY_SPEEDS[i]}
              >
                <div className="workshop-gallery__item" data-clip-up>
                  <img
                    src={src}
                    alt={`Anappuram workshop scene ${i + 1}`}
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--dark ab-values decor-host">
        <div className="decor-grain" aria-hidden="true" />
        <Sawdust count={18} />
        <WoodRings className="wood-rings--left" />
        <div className="container">
          <SectionTitle
            eyebrow="Why Customers Trust Us"
            title="Our Values"
            center
          />
          <div className="feature-grid">
            {VALUES.map((value) => (
              <ValueCard key={value.title} {...value} />
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <CTA
          eyebrow="Work With Us"
          title="Ready to Build Something That Lasts?"
          description="Visit our workshop, or call or WhatsApp us from anywhere in India to start your project."
        />
      </section>
    </div>
  );
}
